import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Flipped, Flipper } from "react-flip-toolkit";
import { makeStyles } from "tss-react/mui";
import { MiLoader } from "@miplatform/components";
import {
  IAttemptListItemResponseForWorkOrder,
  LoaderType,
} from "@miplatform/types";
import Masonry from "@mui/lab/Masonry";
import { Box } from "@mui/material";
import { NHCWorkOrderCard } from "../NHCPriorityScheduler/NHCWorkOrderCard";
import { UnassignedWorkOrdersEmptyState } from "./UnassignedWorkOrdersEmptyState";

const CARD_HEIGHT = 60;
const CARD_WIDTH = 160;
const MASONRY_SPACING_PX = 4;
const MIN_COLUMNS = 1;

export const UnassignedWorkOrdersList = ({
  workOrders,
  loading,
  selectedTabLabel,
  onWorkOrderClick,
  zoom = 1,
  isBulkAssignmentMode = false,
  onCheckboxChange,
  checkedAttemptIds,
  remainingDurationForUser,
  remainingDurationByTemplateId,
  hasAssignableUserColumn,
  ghostOtherWorkOrders = false,
  isNotReadyMode = false,
  selectedUserId,
}: UnassignedWorkOrdersListProps) => {
  const { classes } = useStyles({ cardHeight: zoom * CARD_HEIGHT });
  const listRef = useRef<HTMLDivElement | null>(null);
  const [columns, setColumns] = useState(MIN_COLUMNS);
  const [cardWidth, setCardWidth] = useState(CARD_WIDTH);

  useLayoutEffect(() => {
    const element = listRef.current;
    if (!element) return undefined;

    updateLayoutFromContentWidth(
      measureContentWidth(element),
      setColumns,
      setCardWidth
    );

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      updateLayoutFromContentWidth(
        entry.contentRect.width,
        setColumns,
        setCardWidth
      );
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const flipKey = useMemo(
    () => `${columns}-${zoom}-${workOrders.map((wo) => wo.id).join("-")}`,
    [columns, zoom, workOrders]
  );

  return (
    <Box ref={listRef} className={classes.list}>
      <Box className={classes.loaderContainer}>
        {loading && <MiLoader isLoading type={LoaderType.Linear} />}
      </Box>
      <UnassignedWorkOrdersListContent
        loading={loading}
        workOrders={workOrders}
        selectedTabLabel={selectedTabLabel}
        isNotReadyMode={isNotReadyMode}
        flipKey={flipKey}
        columns={columns}
        cardWidth={cardWidth}
        onWorkOrderClick={onWorkOrderClick}
        zoom={zoom}
        isBulkAssignmentMode={isBulkAssignmentMode}
        onCheckboxChange={onCheckboxChange}
        checkedAttemptIds={checkedAttemptIds}
        remainingDurationForUser={remainingDurationForUser}
        remainingDurationByTemplateId={remainingDurationByTemplateId}
        hasAssignableUserColumn={hasAssignableUserColumn}
        ghostOtherWorkOrders={ghostOtherWorkOrders}
        selectedUserId={selectedUserId}
      />
    </Box>
  );
};

const measureContentWidth = (element: HTMLElement) => {
  const { paddingLeft, paddingRight } = getComputedStyle(element);

  return (
    element.clientWidth - parseFloat(paddingLeft) - parseFloat(paddingRight)
  );
};

const updateLayoutFromContentWidth = (
  contentWidth: number,
  setColumns: (columns: number) => void,
  setCardWidth: (width: number) => void
) => {
  const nextColumns = computeColumns(contentWidth);
  setColumns(nextColumns);
  setCardWidth(computeCardWrapWidth(contentWidth, nextColumns));
};

const computeColumnWidth = (contentWidth: number, columns: number) => {
  if (contentWidth <= 0 || columns <= 0) return CARD_WIDTH;
  const totalSpacing = (columns - 1) * MASONRY_SPACING_PX;

  return (contentWidth - totalSpacing) / columns;
};

const computeCardWrapWidth = (contentWidth: number, columns: number) => {
  const columnWidth = computeColumnWidth(contentWidth, columns);
  const extraSpace = Math.max(0, columnWidth - CARD_WIDTH);

  return Math.floor(CARD_WIDTH + extraSpace);
};

const UnassignedWorkOrderCardItem = ({
  workOrder,
  onWorkOrderClick,
  zoom = 1,
  isBulkAssignmentMode = false,
  onCheckboxChange,
  checkedAttemptIds,
  cardWidth,
  remainingDurationForUser = 0,
  remainingDurationByTemplateId,
  hasAssignableUserColumn = false,
  ghosted = false,
  selectedUserId,
}: UnassignedWorkOrderCardItemProps) => {
  const durationMinutes = workOrder.expectedDuration
    ? workOrder.expectedDuration
    : CARD_HEIGHT;

  const templateRemaining = workOrder.workOrderTemplateId
    ? remainingDurationByTemplateId?.[workOrder.workOrderTemplateId]
    : null;

  const hasCapacity =
    templateRemaining != null
      ? templateRemaining > 0
      : remainingDurationForUser >= durationMinutes;

  const isAvailable = !ghosted && (!hasAssignableUserColumn || hasCapacity);

  const isChecked = checkedAttemptIds?.has(workOrder.id) ?? false;
  const { classes, cx } = useStyles({
    cardHeight: zoom * durationMinutes,
    cardWidth,
    isChecked,
  });

  const handleWorkOrderClick = () => {
    if (ghosted || !isAvailable) {
      return;
    }

    if (isBulkAssignmentMode) {
      const isChecked = checkedAttemptIds?.has(workOrder.id) ?? false;
      onCheckboxChange?.(workOrder, !isChecked);
      return;
    }
    onWorkOrderClick?.(workOrder);
  };

  const handleOnCheckboxChange = () => {
    if (ghosted) {
      return;
    }
    if (!isAvailable && !isChecked) {
      return;
    }
    if (isBulkAssignmentMode) {
      onCheckboxChange?.(workOrder, !isChecked);
    } else {
      onWorkOrderClick?.(workOrder);
    }
  };

  return (
    <Box
      role="button"
      tabIndex={ghosted ? -1 : 0}
      aria-disabled={ghosted || undefined}
      className={cx(
        classes.cardWrap,
        ((!isAvailable && !isChecked) || ghosted) && classes.cardUnavailable
      )}
      onClick={handleWorkOrderClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOnCheckboxChange();
        }
      }}
    >
      <NHCWorkOrderCard
        isBulkAssignmentMode={isBulkAssignmentMode}
        onCheckboxChange={handleOnCheckboxChange}
        checked={isChecked}
        className={cx(classes.card, isChecked && classes.cardChecked)}
        attempt={workOrder}
        loading={false}
        selectedUserId={selectedUserId}
      />
    </Box>
  );
};

type UnassignedWorkOrderCardItemProps = {
  workOrder: IAttemptListItemResponseForWorkOrder;
  cardWidth: number;
  onWorkOrderClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  zoom?: number;
  isBulkAssignmentMode?: boolean;
  onCheckboxChange?: (
    attempt: IAttemptListItemResponseForWorkOrder,
    isChecked: boolean
  ) => void;
  checkedAttemptIds?: ReadonlySet<string>;
  remainingDurationForUser?: number;
  remainingDurationByTemplateId?: Record<string, number>;
  hasAssignableUserColumn?: boolean;
  ghosted?: boolean;
  selectedUserId?: string;
};

const computeColumns = (contentWidth: number) => {
  if (contentWidth <= 0) return MIN_COLUMNS;
  const count = Math.floor(
    (contentWidth + MASONRY_SPACING_PX) / (CARD_WIDTH + MASONRY_SPACING_PX)
  );
  return Math.max(MIN_COLUMNS, count);
};

const UnassignedWorkOrdersListEmptyView = ({
  selectedTabLabel,
  isNotReadyMode = false,
}: {
  selectedTabLabel?: string | null;
  isNotReadyMode?: boolean;
}) => {
  const { classes } = useStyles({ cardHeight: CARD_HEIGHT });

  return (
    <Box className={classes.emptyStateContainer}>
      <UnassignedWorkOrdersEmptyState
        selectedTabLabel={selectedTabLabel}
        isNotReadyMode={isNotReadyMode}
      />
    </Box>
  );
};

const UnassignedWorkOrdersListContent = ({
  loading,
  workOrders,
  selectedTabLabel,
  isNotReadyMode,
  flipKey,
  columns,
  cardWidth,
  onWorkOrderClick,
  zoom = 1,
  isBulkAssignmentMode = false,
  onCheckboxChange,
  checkedAttemptIds,
  remainingDurationForUser,
  remainingDurationByTemplateId,
  hasAssignableUserColumn,
  ghostOtherWorkOrders = false,
  selectedUserId,
}: UnassignedWorkOrdersListProps & {
  flipKey: string;
  columns: number;
  cardWidth: number;
}) => {
  const { classes } = useStyles({ cardHeight: zoom * CARD_HEIGHT });

  if (workOrders.length === 0) {
    return (
      <UnassignedWorkOrdersListEmptyView
        selectedTabLabel={selectedTabLabel}
        isNotReadyMode={isNotReadyMode}
      />
    );
  }

  return (
    <Flipper flipKey={flipKey} spring="gentle" className={classes.flipper}>
      <Masonry columns={columns} spacing={0.5}>
        {workOrders.map((workOrder) => (
          <Flipped key={workOrder.id} flipId={workOrder.id} translate>
            {(flippedProps) => (
              <Box {...flippedProps} className={classes.masonryItem}>
                <UnassignedWorkOrderCardItem
                  workOrder={workOrder}
                  cardWidth={cardWidth}
                  onWorkOrderClick={onWorkOrderClick}
                  zoom={zoom}
                  isBulkAssignmentMode={isBulkAssignmentMode}
                  onCheckboxChange={onCheckboxChange}
                  checkedAttemptIds={checkedAttemptIds}
                  remainingDurationForUser={remainingDurationForUser}
                  remainingDurationByTemplateId={remainingDurationByTemplateId}
                  hasAssignableUserColumn={hasAssignableUserColumn}
                  ghosted={ghostOtherWorkOrders}
                  selectedUserId={selectedUserId}
                />
              </Box>
            )}
          </Flipped>
        ))}
      </Masonry>
    </Flipper>
  );
};

type UnassignedWorkOrdersListProps = {
  workOrders: IAttemptListItemResponseForWorkOrder[];
  loading: boolean;
  selectedTabLabel?: string | null;
  onWorkOrderClick?: (attempt: IAttemptListItemResponseForWorkOrder) => void;
  zoom?: number;
  isBulkAssignmentMode?: boolean;
  onCheckboxChange?: (
    attempt: IAttemptListItemResponseForWorkOrder,
    isChecked: boolean
  ) => void;
  checkedAttemptIds?: ReadonlySet<string>;
  remainingDurationForUser?: number;
  remainingDurationByTemplateId?: Record<string, number>;
  hasAssignableUserColumn?: boolean;
  ghostOtherWorkOrders?: boolean;
  isNotReadyMode?: boolean;
  selectedUserId?: string;
};

const useStyles = makeStyles<{
  cardHeight: number;
  cardWidth?: number;
  isChecked?: boolean;
}>()((theme, { cardHeight, cardWidth, isChecked }) => ({
  list: {
    width: "100%",
    overflow: "auto",
    padding: theme.spacing(2),
  },
  loaderContainer: {
    display: "flex",
    justifyContent: "center",
    width: "100%",
    height: 8,
  },
  emptyStateContainer: {
    display: "flex",
    width: "100%",
    height: "100%",
    minHeight: 120,
  },
  flipper: {
    width: "100%",
  },
  masonryItem: {
    boxSizing: "border-box",
  },
  cardWrap: {
    height: cardHeight,
    cursor: "pointer",
    width: cardWidth ?? CARD_WIDTH,
    transition: theme.transitions.create(["width", "height"], {
      duration: 500,
    }),
  },
  card: {
    width: cardWidth ?? CARD_WIDTH,
    transition: theme.transitions.create(["width"], { duration: 250 }),
  },
  cardChecked: {
    border: `2px solid ${theme.palette.primary.main}`,
  },
  cardUnavailable: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));
